import { useTranslation } from 'react-i18next';
import { Link, NavLink, Outlet } from 'react-router';

import { CONTACT } from './components/base';
import { jetonCourant } from './lib/jeton';
import { useMoi } from './lib/requetes';

function Logo() {
  const { t } = useTranslation();
  // Le nom en écriture maison, fichier de l'outil d'icônes (ODR-0125) : jamais retapé en Inter.
  return (
    <Link to="/" className="inline-block py-1" aria-label={t('app.nom')}>
      <picture>
        <source srcSet="/marque/logo-horizontal-sombre.svg" media="(prefers-color-scheme: dark)" />
        <img src="/marque/logo-horizontal-clair.svg" alt={t('app.nom')} className="h-7 w-auto" />
      </picture>
    </Link>
  );
}

const lien = ({ isActive }: { isActive: boolean }) =>
  isActive ? 'text-texte underline underline-offset-4' : 'text-secondaire hover:text-texte';

function Navigation() {
  const { t } = useTranslation();
  const { data: moi } = useMoi();
  const consenti = moi?.consentement.version === moi?.consentement.versionCourante;
  if (!jetonCourant() || !moi || !consenti) return null;
  return (
    <nav aria-label={t('nav.titre')} className="mt-4 flex flex-wrap gap-x-5 gap-y-2 text-sm">
      <NavLink to="/" end className={lien}>
        {t('nav.aujourdhui')}
      </NavLink>
      <NavLink to="/historique" className={lien}>
        {t('nav.historique')}
      </NavLink>
      {moi.role === 'elle' && (
        <NavLink to="/reglages" className={lien}>
          {t('nav.reglages')}
        </NavLink>
      )}
      <NavLink to="/donnees" className={lien}>
        {t('nav.donnees')}
      </NavLink>
    </nav>
  );
}

function Pied() {
  const { t } = useTranslation();
  return (
    <footer className="border-filet text-secondaire mt-16 border-t pt-6 pb-10 text-sm">
      <div className="flex flex-wrap gap-x-5 gap-y-2">
        <a href={`mailto:${CONTACT}`} className="hover:text-texte">
          {CONTACT}
        </a>
        <Link to="/mentions-legales" className="hover:text-texte">
          {t('pied.mentions')}
        </Link>
        <Link to="/confidentialite" className="hover:text-texte">
          {t('pied.confidentialite')}
        </Link>
      </div>
      {/* Signature obligatoire, jamais traduite (DA, « Pied de page »). */}
      <p className="mt-4">
        Made with{' '}
        <picture>
          <source srcSet="/marque/coeur-petit-sombre.svg" media="(prefers-color-scheme: dark)" />
          <img
            src="/marque/coeur-petit-clair.svg"
            alt="♥"
            width={14}
            height={14}
            className="inline-block align-[-2px]"
          />
        </picture>{' '}
        by{' '}
        <a href="https://sensanetwork.com" className="signature-marque">
          Sensa Network
        </a>
      </p>
    </footer>
  );
}

export function App() {
  const { t } = useTranslation();
  return (
    <div className="bg-fond text-texte min-h-screen antialiased">
      <a
        href="#main"
        className="focus:bg-accent focus:text-sur-accent sr-only focus:not-sr-only focus:absolute focus:top-2 focus:left-2 focus:rounded focus:px-3 focus:py-2"
      >
        {t('app.allerAuContenu')}
      </a>
      <div className="mx-auto max-w-[34rem] px-4 pt-[max(1.5rem,env(safe-area-inset-top))]">
        <header>
          <Logo />
          <Navigation />
        </header>
        <main id="main" tabIndex={-1} className="mt-8 outline-none">
          <Outlet />
        </main>
        <Pied />
      </div>
    </div>
  );
}
